/**
 * GalleryGroupTabs — read-only group pills for the immersive /genies gallery.
 *
 * The live GroupFilterTabs (src/pages/UserHub/MyGenies/components/GroupFilterTabs.tsx)
 * carries management weight that fights the full-bleed gallery: drag-reorder
 * (DndContext), delete dialogs, a "New Group" button, the mobile collapse. The
 * immersive page only needs the *filtering* surface, so this extracts just the
 * tab-rendering — All + per-group + Ungrouped pills with their real counts.
 *
 * Counts mirror GroupFilterTabs exactly: prefer serverGroupCounts (keyed by
 * group id / "ungrouped"), else fall back to counting the visible agents.
 * totalCount is the UNfiltered total so "All" stays correct under filtering.
 *
 * Brand law: genie-* tokens only (active pill -> genie-primary). No raw hex.
 */
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import type { Agent } from "@/types/agent/agent";
import type { AgentGroup } from "@/types/agent/agent-group";

interface GalleryGroupTabsProps {
  groups: AgentGroup[];
  agents: Agent[];
  selectedGroupId: string | null;
  onGroupChange: (groupId: string | null) => void;
  /** UNfiltered total — keeps the "All" pill honest while a filter is active. */
  totalCount?: number;
  serverGroupCounts?: Record<string | number, number>;
}

const pillClass = (isActive: boolean) =>
  cn(
    "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold whitespace-nowrap transition-colors flex-shrink-0",
    isActive
      ? "bg-genie-primary/10 text-genie-primary"
      : "text-gray-500 hover:text-genie-primary hover:bg-genie-primary/5",
  );

const countClass = (isActive: boolean) =>
  cn(
    "px-1.5 py-0.5 text-[11px] font-bold rounded-full",
    isActive ? "bg-genie-primary/15 text-genie-primary" : "bg-gray-100 text-gray-500",
  );

export const GalleryGroupTabs = ({
  groups,
  agents,
  selectedGroupId,
  onGroupChange,
  totalCount,
  serverGroupCounts,
}: GalleryGroupTabsProps) => {
  // Local fallback counts (used only when the server has not provided them yet).
  const localCounts = useMemo(() => {
    const counts: Record<string | number, number> = {};
    agents.forEach((agent) => {
      const key = agent.group_id ?? "ungrouped";
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [agents]);

  const getGroupCount = (groupId: number | null): number => {
    const key = groupId === null ? "ungrouped" : groupId;
    if (serverGroupCounts) return serverGroupCounts[key] ?? 0;
    return localCounts[key] ?? 0;
  };

  const allCount = totalCount ?? agents.length;
  const ungroupedCount = getGroupCount(null);

  // Nothing to filter by — don't render a lone "All" pill.
  if (groups.length === 0 && ungroupedCount === 0) return null;

  return (
    <div
      data-hg-block="group-tabs"
      className="mb-7 flex flex-wrap items-center gap-1.5"
    >
      <button
        type="button"
        onClick={() => onGroupChange(null)}
        className={pillClass(selectedGroupId === null)}
      >
        All
        <span className={countClass(selectedGroupId === null)}>{allCount}</span>
      </button>

      {groups.map((group) => {
        const id = group.id.toString();
        const isActive = selectedGroupId === id;
        return (
          <button
            key={group.id}
            type="button"
            onClick={() => onGroupChange(id)}
            className={pillClass(isActive)}
          >
            {group.name}
            <span className={countClass(isActive)}>{getGroupCount(group.id)}</span>
          </button>
        );
      })}

      {ungroupedCount > 0 && (
        <button
          type="button"
          onClick={() => onGroupChange("ungrouped")}
          className={pillClass(selectedGroupId === "ungrouped")}
        >
          Ungrouped
          <span className={countClass(selectedGroupId === "ungrouped")}>
            {ungroupedCount}
          </span>
        </button>
      )}
    </div>
  );
};

export default GalleryGroupTabs;
